// Client-side password strength estimation via zxcvbn-ts. The dictionaries are
// heavy (~hundreds of KB), so they're dynamically imported once on first use —
// keeping them out of the initial bundle. Server-side enforcement is a lighter
// structural rule in @shared/schemas (weakPasswordReason); this drives the UI
// meter and the client-side submit gate.

export type PasswordScore = 0 | 1 | 2 | 3 | 4;

export interface PasswordStrength {
  score: PasswordScore;
  /** Short next-step tip for the user, if zxcvbn produced one. */
  suggestion: string | null;
  /** Why the password is weak, if flagged. */
  warning: string | null;
}

// Passwords scoring below this are rejected at submit. 2 = "somewhat guessable",
// a reasonable floor that still blocks the common/obvious cases without demanding
// a password manager.
export const MIN_PASSWORD_SCORE: PasswordScore = 2;

export const STRENGTH_LABELS: Record<PasswordScore, string> = {
  0: "Sangat lemah",
  1: "Lemah",
  2: "Sederhana",
  3: "Kuat",
  4: "Sangat kuat",
};

// Tailwind classes for the meter fill, indexed by score.
export const STRENGTH_COLORS: Record<PasswordScore, string> = {
  0: "bg-rose-500",
  1: "bg-rose-400",
  2: "bg-amber-400",
  3: "bg-emerald-400",
  4: "bg-emerald-500",
};

type Zxcvbn = (password: string, userInputs?: (string | number)[]) => {
  score: number;
  feedback: { warning: string | null; suggestions: string[] };
};

let loader: Promise<Zxcvbn> | null = null;

// Load + configure zxcvbn once, then reuse. Concurrent callers share the promise.
async function getZxcvbn(): Promise<Zxcvbn> {
  if (!loader) {
    loader = (async () => {
      const [{ zxcvbn, zxcvbnOptions }, common, en] = await Promise.all([
        import("@zxcvbn-ts/core"),
        import("@zxcvbn-ts/language-common"),
        import("@zxcvbn-ts/language-en"),
      ]);
      zxcvbnOptions.setOptions({
        dictionary: { ...common.dictionary, ...en.dictionary },
        graphs: common.adjacencyGraphs,
        translations: en.translations,
      });
      return zxcvbn as Zxcvbn;
    })();
  }
  return loader;
}

/**
 * Estimate a password's strength. `userInputs` (email, name, subdomain…) are fed
 * to zxcvbn so passwords derived from the user's own details score lower.
 */
export async function scorePassword(
  password: string,
  userInputs: (string | number)[] = [],
): Promise<PasswordStrength> {
  if (!password) return { score: 0, suggestion: null, warning: null };
  const zxcvbn = await getZxcvbn();
  const res = zxcvbn(password, userInputs.filter(Boolean));
  return {
    score: Math.max(0, Math.min(4, res.score)) as PasswordScore,
    suggestion: res.feedback.suggestions[0] ?? null,
    warning: res.feedback.warning || null,
  };
}
